import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import ParticleField from "../common/ParticleField";
import NeuralHero from "../three/NeuralHero";
import PillButton from "../common/PillButton";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const headlineLines = ["Engineering the Future", "with AI, Data, Cloud", "& Talent"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden px-6 pt-28"
      data-testid="hero"
    >
      {/* Background layers */}
      <motion.div style={{ y: yVisual }} className="pointer-events-none absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg"
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/80 to-ink-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/30 to-transparent" />
      </motion.div>
      <div className="absolute inset-0 z-0 opacity-60">
        <ParticleField />
      </div>
      {/* Premium 3D neural-mind visual (right side on desktop) */}
      <NeuralHero className="z-0 opacity-90 lg:left-[38%]" />
      <div className="pointer-events-none absolute left-[10%] top-1/3 z-0 h-[420px] w-[520px] radial-glow" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium tracking-wide text-slate-300 backdrop-blur"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-sky-400" />
          AI + Data + Cloud + Engineering + Talent
        </motion.div>

        <h1 className="max-w-5xl font-heading text-5xl font-bold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
          {headlineLines.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 1 ? (
                  <span className="text-gradient">{l}</span>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 lg:text-lg"
        >
          AMVI Technologies helps organizations accelerate digital transformation
          through AI, data, cloud, engineering, and specialized talent solutions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <PillButton to="/contact" size="lg" testId="hero-talk-expert">
            Talk to an Expert
          </PillButton>
          <PillButton variant="outline" size="lg" to="/solutions" testId="hero-explore-solutions">
            Explore Solutions
          </PillButton>
        </motion.div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 z-10 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
}
